import React from 'react';
import { useSpring } from '@react-spring/web';

import debounce from 'debounce';
import { faLeft } from '@fortawesome/pro-solid-svg-icons/faLeft';
import { faRight } from '@fortawesome/pro-solid-svg-icons/faRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button } from '@wildidea/components/Button/Button';
import { Card } from '@wildidea/components/Card/Card';
import { useConst } from '@wildidea/components/hooks/useConst';

import { PhoneFrame } from '@components/PhoneFrame.js';

import * as css from './ScreenshotCarousel.module.css';

export interface ScreenshotCarouselProps {
  header: React.ReactNode;
  items: {
    screenshot: string;
    alt: string;
    className?: string;
    annotations: {
      header: React.ReactNode;
      className?: string;
      children: React.ReactNode;
    }[];
    phonePlacement: 'start' | 'end';
  }[];
  className?: string;
}

export const ScreenshotCarousel = React.forwardRef((({ header, items, className }, ref) => {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [stop, setStop] = React.useState(0);
  const springAnimationIdRef = React.useRef<number | null>(null);
  const isAnimatingNativeRef = React.useRef(false);
  const lastScrollToRef = React.useRef(0);

  const readState = (el: HTMLElement | null) => {
    const viewportWidth =
      window.visualViewport?.width || window.document.documentElement.clientWidth;
    const stopSize = viewportWidth < 1200 ? viewportWidth : viewportWidth / 2;
    const currentScroll = el?.scrollLeft || 0;
    const currentStop = Math.round(currentScroll / stopSize);
    return {
      viewportWidth,
      stopSize,
      currentScroll,
      currentStop
    };
  };

  const { viewportWidth, stopSize } = readState(scrollContainerRef.current);
  const scrollTo = stop * stopSize;
  const numStops = scrollContainerRef.current
    ? viewportWidth < 1200
      ? items.length
      : items.length % 2 === 0
        ? items.length - 1
        : items.length
    : 1;

  const onNativeSnapFinish = useConst(
    debounce(() => {
      scrollContainerRef.current?.removeEventListener('scroll', onNativeSnapFinish);
      isAnimatingNativeRef.current = false;
    }, 100)
  );

  const snap = useConst(
    debounce(() => {
      const el = scrollContainerRef.current;
      if (!el) return;

      const { stopSize, currentScroll, currentStop } = readState(el);

      const scrollTo = currentStop * stopSize;

      if (Math.abs(Math.floor(scrollTo) - Math.floor(currentScroll)) > 2) {
        isAnimatingNativeRef.current = true;
        setStop(currentStop);
        scrollContainerRef.current.scrollTo({ left: scrollTo, behavior: 'smooth' });
        scrollContainerRef.current.removeEventListener('scroll', onNativeSnapFinish);
        scrollContainerRef.current.addEventListener('scroll', onNativeSnapFinish);
        lastScrollToRef.current = scrollTo;
      } else {
        lastScrollToRef.current = currentScroll;
        setStop(currentStop);
      }
    }, 100)
  );

  const [_, api] = useSpring(() => ({
    id: 0,
    scrollTo: lastScrollToRef.current,
    onChange: (spring) => {
      scrollContainerRef.current?.scroll({ left: spring.value.scrollTo, behavior: 'instant' });
    },
    onRest: (spring) => {
      window.setTimeout(() => {
        if (springAnimationIdRef.current === spring.value.id) {
          springAnimationIdRef.current = null;
        }
      }, 500);
    }
  }));

  React.useEffect(
    function animateSpringScroll() {
      if (
        Math.floor(scrollTo) !== Math.floor(lastScrollToRef.current) &&
        !isAnimatingNativeRef.current
      ) {
        springAnimationIdRef.current = springAnimationIdRef.current
          ? springAnimationIdRef.current + 1
          : 1;
        api.start({
          id: springAnimationIdRef.current,
          scrollTo,
          from: { scrollTo: scrollContainerRef.current?.scrollLeft || lastScrollToRef.current }
        });
        lastScrollToRef.current = scrollTo;
      }
    },
    [scrollTo]
  );

  const onScroll = useConst(() => {
    if (!springAnimationIdRef.current) {
      snap();
    }
  });

  return (
    <section
      className={[css.carousel, stop % 2 === 0 ? css.scrollEven : css.scrollOdd, className]
        .filter(Boolean)
        .join(' ')}
      ref={ref}
      suppressHydrationWarning
    >
      <div className={css.header}>{header}</div>
      <div className={css.items} onScroll={onScroll} ref={scrollContainerRef}>
        {items.map((item, index) => (
          <div
            className={[css.carouselItem, css[item.phonePlacement], item.className]
              .filter(Boolean)
              .join(' ')}
            key={index}
          >
            <div className={css.phoneFrame}>
              <PhoneFrame className={css.phone}>
                <img src={item.screenshot} className={css.screenshot} alt={item.alt} />
              </PhoneFrame>
              <div className={css.annotations}>
                {item.annotations.map(({ header, className, children }, index) => (
                  <Card
                    color="info"
                    header={header}
                    className={[css.annotation, className].filter(Boolean).join(' ')}
                    key={index}
                  >
                    <div className={css.annotationInner}>{children}</div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      {numStops !== 1 ? (
        <div className={css.controls}>
          <div
            className={[
              css.controlsInner,
              stop === 0 || stop === numStops - 1 ? css.one : css.two,
              stop === 0 ? css.right : stop === numStops - 1 ? css.left : null
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {stop !== 0 ? (
              <>
                <div className={[css.pips, css.pipsLeft].filter(Boolean).join(' ')}>
                  {Array.from(new Array(Math.max(0, stop))).map((_, index) => (
                    <div className={css.pip} key={index} />
                  ))}
                </div>
                <Button
                  aria-label="Back"
                  appearance="outline"
                  className={css.back}
                  onMouseDown={() => {
                    setStop(stop - 1);
                  }}
                >
                  <FontAwesomeIcon icon={faLeft} />
                </Button>
              </>
            ) : null}
            {stop !== numStops - 1 ? (
              <>
                <Button
                  aria-label="Next"
                  appearance="outline"
                  className={css.next}
                  onMouseDown={() => {
                    setStop(stop + 1);
                  }}
                >
                  <FontAwesomeIcon icon={faRight} />
                </Button>
                <div className={[css.pips, css.pipsRight].filter(Boolean).join(' ')}>
                  {Array.from(new Array(Math.max(0, numStops - 1 - stop))).map((_, index) => (
                    <div className={css.pip} key={index} />
                  ))}
                </div>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}) satisfies React.ForwardRefRenderFunction<HTMLDivElement, ScreenshotCarouselProps>);
